/**
 * Utility functions for exporting data to CSV format
 */

/**
 * Convert array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} headers - Array of header names (keys)
 * @returns {string} CSV formatted string
 */
export const convertToCSV = (data, headers) => {
    if (!data || data.length === 0) {
        return "";
    }

    // Create header row
    const headerRow = headers.map(h => `"${h}"`).join(",");

    // Create data rows
    const dataRows = data.map(item => {
        return headers.map(header => {
            let value = item[header] || "";
            // Escape quotes and wrap in quotes if contains comma
            if (typeof value === "string") {
                value = value.replace(/"/g, '""');
                if (value.includes(",") || value.includes('"') || value.includes("\n")) {
                    value = `"${value}"`;
                }
            }
            return value;
        }).join(",");
    });

    return [headerRow, ...dataRows].join("\n");
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name of the file to download
 */
export const downloadCSV = (csvContent, filename = "export.csv") => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

/**
 * Export array of objects to CSV file
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header names
 * @param {string} filename - Name of the file to download
 */
export const exportToCSV = (data, headers, filename = "export.csv") => {
    const csvContent = convertToCSV(data, headers);
    downloadCSV(csvContent, filename);
};

/**
 * Format date for display in CSV
 * @param {Date|string} date - Date to format
 * @returns {string} Formatted date string
 */
export const formatDateForCSV = (date) => {
    if (!date) return "";
    if (typeof date === "string") return date;
    return new Date(date).toISOString().split("T")[0];
};

/**
 * Format currency value
 * @param {number} value - Value to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, decimals = 2) => {
    if (!value && value !== 0) return "";
    return parseFloat(value).toFixed(decimals);
};
